import React, { useEffect, useRef } from "react";
import { useIntl, FormattedNumber } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";

//Components
import {
    /* usePagination, */
    TableButtons,
    SearchInput,
    IconButton,
    TableView,
} from "@gpstrackit/gtc-react-controls";
import { Grid } from "@mui/material";

//Hooks
import {
    getMaintenanceLabel,
    formatCatalogDate,
    getMaintenanceBar,
    statusPill,
} from '../../../../../../core/hooks/utils';
import { usePaggination } from '../../../../../../core/hooks';

//Actions
import {
    maintenanceDDSDLTSearchDriverScheduledTasks,
    reset,
} from './redux/slice';

//Views
import EntityDetails from '../../../../modals/entityDetails';

const ScheduleTasksLogs = (props) => {
    const { refreshTable, view, handleDetails } = props;

    const { messages, locale, formatNumber } = useIntl();
    const dispatch = useDispatch();
    const location = useLocation();
    const tableRef = useRef();

    const pagination = usePaggination(null, null, true, tableRef);

    //Redux States
    const {
        loadingScheduledTasksLogs,
        scheduledTasksLogs,
        error,
    } = useSelector(state => state.maintenanceScheduleTasksLogsTabRedux);
    const openEntityDetails = useSelector(state => state.maintenanceEntityDetailsRedux.open);

    let path = location.pathname.split("/");
    const id = path[path.length - 1];

    useEffect(() => {
        dispatch(maintenanceDDSDLTSearchDriverScheduledTasks({ driverId: id }));
        return () => {
            pagination.onChangeData([]);
            dispatch(reset());
        }
    }, [])

    useEffect(() => {
        if (error) {
            dispatch(reset({ error: null }))
        }
    }, [error])

    useEffect(() => {
        pagination.setColumns([
            {
                key: "scheduledTaskName",
                show: true,
                title: messages["driversTasksTab_task"],
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <Grid container alignItems="center">
                            <Grid item xs={12}>
                                <span>{row?.scheduledTaskName}</span>
                            </Grid>
                        </Grid>
                    );
                },
            },
            {
                key: "dueDate",
                show: true,
                title: messages["driversTasksTab_due"],
                export: true,
                sort: true,
                render: (row) => {
                    return (
                        <>
                            {row?.startDate ? (
                                getMaintenanceBar("time", "Done", row.startDate, row.dueDate, row.logDate, messages)
                            ) : ""}
                        </>
                    );
                },
                sortValue: (row) => {
                    return row?.startDate ? getMaintenanceLabel("time", row.startDate, row.dueDate, row.logDate, messages) : null
                }
            },
            {
                key: "logDate",
                show: true,
                title: messages["driversTasksTab_performed_at"],
                export: true,
                sort: true,
                render: (row) => {
                    const dateFormated = formatCatalogDate(row.logDate, null, locale);
                    let date = dateFormated.split(",")[1]
                    return (
                        <>
                            {dateFormated && (
                                <div style={{ width: "180px" }}>
                                    <span>{date}</span>
                                    <br />
                                    <span className="subtitle">
                                        {moment(dateFormated.split(",")[0]).locale(locale).format("MMMM DD, YYYY")}
                                    </span>
                                </div>
                            )}
                        </>
                    );
                },
                sortValue: (row) => {
                    if (row?.logDate) {
                        return row?.logDate
                    }
                }
            },
            {
                key: "estimatedCost",
                sort: true,
                show: true,
                title: messages["driversTasksTab_cost"],
                export: true,
                sort: true,
                render: (row) => {
                    return row.actualCost ? (
                        <FormattedNumber
                            value={row.actualCost}
                            style="currency"
                            currency="USD"
                        />
                    ) : <></>
                },
                sortValue: (row) => {
                    const format = { style: 'currency', currency: 'USD' };
                    const numberFormat = new Intl.NumberFormat('en-US', format);
                    return row?.actualCost ? numberFormat.format(row?.actualCost) : '0';
                }
            },
            {
                key: "status",
                show: true,
                title: messages["driversTasksTab_status"],
                export: true,
                render: () => <>{statusPill({ status: "Done" })}</>
            },
            {
                key: "notes",
                show: true,
                title: messages["driversTasksTab_notes"],
                export: true,
                render: (row) => {
                    if (row.notes && row.id) {
                        const { notes, id } = row;
                        return (
                            <>
                                {notes ? (
                                    <IconButton
                                        placement="bottom"
                                        variant="secondary"
                                        icon="comment"
                                        size="small"
                                        disabled={loadingScheduledTasksLogs}
                                        onClick={() => handleDetails(id, 'DriverScheduledTaskLog', 0, row.scheduledTaskName)}
                                    />
                                ) : ""}
                            </>
                        );
                    }
                },
            },
            {
                key: "attachments",
                show: true,
                title: messages["driversTasksTab_attachments"],
                export: true,
                render: (row) => {
                    if (row.fileIds && row.id) {
                        const { fileIds, id } = row;
                        return (
                            <>
                                {fileIds && fileIds.length > 0 ? (
                                    <IconButton
                                        placement="bottom"
                                        variant="secondary"
                                        icon="attach_file"
                                        size="small"
                                        disabled={loadingScheduledTasksLogs}
                                        onClick={() => handleDetails(id, 'DriverScheduledTaskLog', 1, row.scheduledTaskName)}
                                    />
                                ) : ""}
                            </>
                        );
                    }
                }
            }
        ])
    }, [locale])

    useEffect(() => {
        if (scheduledTasksLogs) {
            pagination.onChangeData(scheduledTasksLogs);
        }
    }, [scheduledTasksLogs])

    const getDataToExport = () => {
        let values = []
        let allData = pagination?.allData
        if (allData) {
            allData.forEach((item) => {
                let scheduledTaskName = item?.scheduledTaskName
                let due = item?.startDate ? getMaintenanceLabel("time", item.startDate, item.dueDate, item.logDate, messages) : ""
                let logDate = item.logDate ? moment(item.logDate).format("DD/MM/YYYY") : "";
                const format = { style: 'currency', currency: 'USD' };
                const numberFormat = new Intl.NumberFormat('en-US', format);
                let actualCost = item?.actualCost ? numberFormat.format(item?.actualCost).replace(/,/g, '.')  : "";
                let notes = item.notes
                let fileIds = item.fileIds?.length || ""
                values.push([scheduledTaskName, due, logDate, actualCost, "Done", notes, fileIds])
            })
        }
        return values
    }

    return (
        <>
            <Grid
                container
                direction="row"
                justifyContent="flex-start"
                alignItems="center"
                spacing={1}
            >
                <Grid item xs={6} >
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: 'flex', paddingTop: 0 }}>
                            <IconButton
                                icon="event"
                                style={{ padding: 5, marginRight: 5 }}
                                className={view === 'scheduleTask' ? 'selected-driver-detail' : ''}
                                onClick={() => refreshTable("scheduleTask")}
                                tooltip={messages["driver_detail_schedule_tasks"]}
                            />
                            <IconButton
                                icon="check_circle"
                                style={{ padding: 5, marginRight: 5 }}
                                className={view === 'scheduleTaskLog' ? 'selected-driver-detail' : ''}
                                onClick={() => refreshTable("scheduleTaskLog")}
                                tooltip={messages["driver_detail_schedule_tasks_logs"]}
                            />
                            <TableButtons
                                tableRef={tableRef}
                                buttons={["export"]}
                                onExport={(type) => {
                                    if (type === "Csv") tableRef.current?.exportCSV()
                                    else tableRef.current?.exportPDF()
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={6} style={{ paddingTop: 5 }} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['search']}
                        searchText={pagination.searchText}
                        onSearchText={pagination.onSearchChange}
                    />
                </Grid>
            </Grid>
            <TableView
                ref={tableRef}
                loading={loadingScheduledTasksLogs}
                hideDragable
                keyColumn="id"
                hideAllInPagination
                disableItemSelection
                topheight={220}
                {...pagination}
                getDataToExport={(data) => getDataToExport(data)}
                exportName="schedule_tasks_logs"
                exportOrientation="landscape"
            />
            {openEntityDetails && <EntityDetails />}
        </>
    )
}
export default ScheduleTasksLogs;